/**
 * Escapes special characters in a string to prevent XSS.
 * This should be used for any user-provided content that is rendered
 * via dangerouslySetInnerHTML or similar.
 */
export function escapeHtml(unsafe: string): string {
	if (!unsafe) return "";
	return unsafe
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;")
		.replace(/`/g, "&#96;");
}

/**
 * Safely serializes data to JSON for use inside <script> tags.
 * Escapes special characters < and > to prevent XSS.
 */
export function safeJSON(data: unknown): string {
	return JSON.stringify(data).replace(/[<>]/g, c => {
		return c === "<" ? "\\u003c" : "\\u003e";
	});
}

/**
 * Masks a secret value to allow for correlation without exposing the full secret.
 * Always returns "REDACTED" to prevent information leakage (length, prefix) and URL encoding issues.
 */
function maskSecret(_value: string): string {
	return "REDACTED";
}

/**
 * Redacts sensitive query parameters from a URL string to prevent sensitive data leakage in logs.
 * Supports absolute URLs and relative path+search strings.
 *
 * @param urlStr The URL string to redact (absolute or relative)
 * @returns The redacted URL string
 */
export function redactSensitiveParams(urlStr: string): string {
	if (!urlStr) return "";

	// List of case-insensitive keys to redact
	const sensitiveKeys = [
		"token",
		"password",
		"secret",
		"authorization",
		"tid",
		"access_token",
		"refresh_token",
		"api_key",
		"apikey",
		"otp",
		"client_secret",
		"session",
		"cookie",
		"auth",
		"key",
		"id_token",
	];

	// Try to parse as a valid URL first
	try {
		// This will throw for relative URLs or invalid URLs
		const url = new URL(urlStr);
		let changed = false;
		url.searchParams.forEach((value, key) => {
			if (sensitiveKeys.includes(key.toLowerCase())) {
				url.searchParams.set(key, maskSecret(value));
				changed = true;
			}
		});
		if (changed) {
			return url.toString();
		}
		// If no changes, return original to preserve formatting quirks if any
		return urlStr;
	} catch {
		// Fall through to manual parsing if URL parsing fails.
		// Relative URLs (e.g. "/api/foo?token=123") will fail the URL constructor
		// and fall through to this block, preserving their relative path structure.
		// This also handles malformed absolute URLs that contain secrets.
	}

	// Manual parsing logic
	// Split at the first '?' to separate path and query string
	const queryIndex = urlStr.indexOf("?");
	if (queryIndex === -1) {
		return urlStr;
	}

	try {
		const path = urlStr.substring(0, queryIndex);
		const search = urlStr.substring(queryIndex); // includes leading ?
		const searchParams = new URLSearchParams(search);
		let changed = false;

		// Iterate over keys to check against sensitive list
		searchParams.forEach((value, key) => {
			if (sensitiveKeys.includes(key.toLowerCase())) {
				searchParams.set(key, maskSecret(value));
				changed = true;
			}
		});

		if (!changed) {
			return urlStr;
		}

		// Reconstruct URL: path + '?' + encoded params
		const newSearch = searchParams.toString();
		return path + "?" + newSearch;
	} catch {
		// If even manual parsing fails (highly unlikely for string manipulation), return original
		return urlStr;
	}
}
